import { useTranslation } from "react-i18next";
import { LifeArea } from "./WheelOfLife";

interface WheelVisualizationProps {
  lifeAreas: LifeArea[];
}

export function WheelVisualization({ lifeAreas }: WheelVisualizationProps) {
  const { t } = useTranslation();

  // Configuration - based on your radial chart, sized for A4
  const margin = { top: 40, right: 40, bottom: 40, left: 40 };
  const barHeight = 300; // Increased significantly for A4 size
  const domain = [0, 10]; // 0-10 scale for ratings
  const tickCircleValues = [2, 4, 6, 8, 10]; // Rating levels to show circles
  const transitionDuration = 1000;

  // Calculate dimensions
  const numBars = lifeAreas.length;
  const labelRadius = barHeight * 1.025;
  const svgSize = 2 * barHeight + margin.left + margin.right;
  const center = svgSize / 2;

  // Scale function to convert rating to radius
  const barScale = (value: number) => {
    return (value / (domain[1] - domain[0])) * barHeight;
  };

  // Helper function to create arc path - fixed coordinate system
  const createArcPath = (
    startAngle: number,
    endAngle: number,
    innerRadius: number,
    outerRadius: number
  ) => {
    // Convert to radians and adjust for SVG coordinate system
    const startRad = (startAngle * Math.PI) / 180 - Math.PI / 2;
    const endRad = (endAngle * Math.PI) / 180 - Math.PI / 2;

    // Use the actual center of the SVG (no offset needed)
    const startX = center + innerRadius * Math.cos(startRad);
    const startY = center + innerRadius * Math.sin(startRad);
    const endX = center + outerRadius * Math.cos(endRad);
    const endY = center + outerRadius * Math.sin(endRad);

    const largeArcFlag = endRad - startRad <= Math.PI ? "0" : "1";

    return [
      `M ${startX} ${startY}`,
      `L ${center + outerRadius * Math.cos(startRad)} ${
        center + outerRadius * Math.sin(startRad)
      }`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
      `L ${center + innerRadius * Math.cos(endRad)} ${
        center + innerRadius * Math.sin(endRad)
      }`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${startX} ${startY}`,
      "Z",
    ].join(" ");
  };

  // Calculate label positions - fixed coordinate system
  const getLabelPosition = (index: number, radius: number) => {
    const angle = (index * 360) / numBars;
    const angleRad = (angle * Math.PI) / 180 - Math.PI / 2;
    const x = center + radius * Math.cos(angleRad);
    const y = center + radius * Math.sin(angleRad);
    return { x, y, angle };
  };

  // Generate screen reader description of the wheel
  const generateWheelDescription = () => {
    const areas = lifeAreas
      .map((area) => `${area.name}: ${area.rating} out of 10`)
      .join(", ");

    return `Wheel of Life chart showing 12 life areas. ${areas}. The further from the center, the higher the satisfaction level.`;
  };

  return (
    <div className="flex flex-col items-center space-y-6">
      <div
        className="relative"
        style={{ minWidth: "700px", overflow: "scroll" }}
      >
        <svg
          width={svgSize}
          height={svgSize}
          className="drop-shadow-lg"
          style={{
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSize: "12px",
            maxWidth: "100%",
            height: "auto",
          }}
          role="img"
          aria-labelledby="wheel-title"
          aria-describedby="wheel-description"
          aria-label="Wheel of Life visualization chart"
        >
          <title id="wheel-title">{t("wheel.title")}</title>
          <desc id="wheel-description">{generateWheelDescription()}</desc>

          {/* Concentric circles at tick values */}
          <g className="tick-circles" aria-hidden="true">
            {tickCircleValues.map((value) => (
              <circle
                key={value}
                cx={center}
                cy={center}
                r={barScale(value)}
                fill="none"
                stroke="#ddd"
                strokeWidth="2"
                strokeDasharray="4, 4"
              />
            ))}
          </g>

          {/* Spokes (radial lines) */}
          <g className="spokes" aria-hidden="true">
            {lifeAreas.map((_, index) => {
              const angle = (index * 360) / numBars;
              const angleRad = (angle * Math.PI) / 180 - Math.PI / 2;
              const endX = center + barHeight * Math.cos(angleRad);
              const endY = center + barHeight * Math.sin(angleRad);

              return (
                <line
                  key={index}
                  x1={center}
                  y1={center}
                  x2={endX}
                  y2={endY}
                  stroke="#666"
                  strokeWidth="1"
                />
              );
            })}
          </g>

          {/* Life area segments - using custom colors */}
          {lifeAreas.map((area, index) => {
            const startAngle = (index * 360) / numBars;
            const endAngle = ((index + 1) * 360) / numBars;
            const outerRadius = barScale(area.rating);
            const innerRadius = 0;
            const color = area.color || "#3b82f6"; // Use custom color or fallback

            return (
              <path
                key={area.id}
                d={createArcPath(
                  startAngle,
                  endAngle,
                  innerRadius,
                  outerRadius
                )}
                fill={color}
                stroke="white"
                strokeWidth="1"
                className="opacity-80 hover:opacity-100 transition-opacity"
                aria-label={`${area.name} segment with rating ${area.rating} out of 10`}
                role="img"
                // tabIndex={0}
                // onKeyDown={(e) => {
                //   if (e.key === 'Enter' || e.key === ' ') {
                //     e.preventDefault();
                //     // Could add focus management or additional interactions here
                //   }
                // }}
              />
            );
          })}

          {/* Outer circle */}
          <circle
            cx={center}
            cy={center}
            r={barHeight}
            stroke="#aaa"
            strokeWidth="1"
            className="outer"
            fill="none"
            aria-hidden="true"
          />

          {/* Rating labels on circles */}
          {tickCircleValues.map((value) => {
            const pos = getLabelPosition(0, barScale(value));
            return (
              <text
                key={value}
                x={pos.x}
                y={pos.y - 10}
                textAnchor="middle"
                className="text-sm fill-muted-foreground font-medium"
                aria-hidden="true"
              >
                {value}
              </text>
            );
          })}

          {/* Area labels around the circle - curved text path */}
          <g
            className="labels"
            transform={`translate(${center}, ${center})`}
            aria-hidden="true"
          >
            {/* Define the curved path for labels */}
            <defs>
              <path
                id="label-path"
                d={`m0 -${labelRadius} a${labelRadius} ${labelRadius} 0 1,1 -0.01 0`}
                fill="none"
              />
            </defs>

            {/* Labels using textPath */}
            {lifeAreas.map((area, index) => {
              const startOffset = (index * 100) / numBars + 50 / numBars;

              return (
                <text
                  key={area.id}
                  style={{
                    textAnchor: "middle",
                    fill: "#333",
                    fontSize: "10px",
                  }}
                  className="text-xsss font-small"
                >
                  <textPath href="#label-path" startOffset={`${startOffset}%`}>
                    {area.name.slice(0, 25).toUpperCase()}
                  </textPath>
                </text>
              );
            })}
          </g>
        </svg>
      </div>

      <div className="text-center space-y-2">
        <h3>{t("wheel.title")}</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {t("wheel.description")}
        </p>
      </div>
    </div>
  );
}
